import React from 'react';
import { Badge } from 'react-bootstrap';
import { FaShieldAlt, FaServer, FaCheckCircle, FaChevronRight } from 'react-icons/fa';
import AppVersion from '../../../../config/AppVersion';
import AboutModal from '../About/AboutModal';
import apiService from '../../../../services/apiServices';

const Footer = () => {
  const [showAbout, setShowAbout] = React.useState(false);
  const [info, setInfo] = React.useState(null);

  React.useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await apiService.get('/system/info');
        setInfo(res.data);
      } catch (err) {
        console.error('Failed to load footer info', err);
      }
    };
    fetchInfo();
  }, []);

  const remainingDays = info?.remainingDays ?? 0;
  const isHealthy = remainingDays > 30;

  return (
    <footer 
      className="content-footer footer no-print" 
      style={{ 
        background: '#ffffff', 
        borderTop: '1px solid #eef2f6',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.02)',
        padding: '14px 0'
      }}
    >
      <div className="container-xxl px-3 px-md-4">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          
          {/* Left Column: Brand & Architecture Identity */}
          <div className="d-flex align-items-center flex-wrap justify-content-center justify-content-md-start gap-2">
            <div className="d-flex align-items-center gap-2">
              <div 
                className="rounded-2 d-flex align-items-center justify-content-center text-white shadow-2xs"
                style={{ 
                  width: '24px', 
                  height: '24px', 
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                  fontSize: '0.75rem',
                  fontWeight: 900 
                }}
              >
                iS
              </div>
              <span className="fw-bold text-dark" style={{ fontSize: '0.82rem', letterSpacing: '-0.2px' }}>
                iShopMaster
              </span>
              <span 
                className="badge px-1.5 py-0.5 rounded" 
                style={{ 
                  fontSize: '0.62rem', 
                  fontWeight: 800, 
                  letterSpacing: '0.5px',
                  background: '#f1f5f9', 
                  color: '#475569',
                  border: '1px solid #e2e8f0'
                }}
              >
                PRO
              </span>
            </div>

            <span className="text-muted d-none d-sm-inline opacity-40">|</span>

            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              © {new Date().getFullYear()}
            </span>

            <span className="text-muted d-none d-sm-inline opacity-40">•</span>

            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              Engineered by{' '}
              <button
                type="button"
                onClick={() => setShowAbout(true)}
                className="btn btn-link p-0 fw-bold text-decoration-none hover-link"
                style={{ fontSize: '0.78rem', color: '#2563eb' }}
              >
                JJSOFT GLOBAL
              </button>
            </span>
          </div>

          {/* Right Column: SaaS Operational & License Telemetry */}
          <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
            
            {/* Live Operational Status */}
            <div 
              className="d-inline-flex align-items-center gap-1.5 px-2.5 py-1 rounded-pill"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
            >
              <span className="status-pulse-dot" />
              <span className="text-secondary fw-semibold" style={{ fontSize: '0.72rem', letterSpacing: '0.2px' }}>
                Operational
              </span>
            </div>

            {/* Version Pill */}
            <div className="version-pill-wrapper">
              <AppVersion />
            </div>

            {/* License Capsule Action */}
            <button
              type="button"
              onClick={() => setShowAbout(true)}
              className="license-capsule-btn d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill border-0 shadow-2xs"
              style={{
                background: isHealthy ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
                color: '#ffffff',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <FaShieldAlt size={10} color={isHealthy ? '#4ade80' : '#fca5a5'} />
              <span>
                {info?.remainingDays !== undefined ? `${info.remainingDays}d License` : 'Secured Node'}
              </span>
              <FaChevronRight size={8} className="opacity-60 ms-0.5" />
            </button>

          </div>

        </div>
      </div>

      <AboutModal show={showAbout} onHide={() => setShowAbout(false)} />

      <style>
        {`
          .hover-link:hover {
            color: #1d4ed8 !important;
            text-decoration: underline !important;
          }

          .license-capsule-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
            filter: brightness(1.08);
          }

          .license-capsule-btn:active {
            transform: translateY(0);
          }

          .status-pulse-dot {
            width: 7px;
            height: 7px;
            background-color: #10b981;
            border-radius: 50%;
            display: inline-block;
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
            animation: pulse-emerald 2s infinite;
          }

          @keyframes pulse-emerald {
            0% {
              box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
            }
            70% {
              box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
            }
          }

          .version-pill-wrapper small {
            padding: 2px 8px !important;
            margin: 0 !important;
            font-size: 0.72rem !important;
            background: #f1f5f9 !important;
            color: #475569 !important;
            border-radius: 50px !important;
            border: 1px solid #e2e8f0 !important;
            font-weight: 600 !important;
            display: inline-block !important;
          }
        `}
      </style>
    </footer>
  );
};

export default Footer;